/**
 * Sample contractor resume evaluation (hardcoded for frontend preview).
 * Replace with real API response when backend is in use.
 */
export const SAMPLE_CONTRACTOR_EVALUATION = `# 📋 Resume Evaluation — Alexis Tang
> **For Internal Use Only**

---

## SECTION 1: BANNED CLIENT CHECK ⚠️ FLAGGED

**Walmart** appears in:
- Professional Summary: *"Currently contributing to Walmart's EXPO experimentation platform..."*
- Work Experience: Listed as employer "WALMART, Sunnyvale, CA" (Sep 2022 – Present)

> ⚠️ Walmart must be removed/replaced before submission. Candidate having worked there is fine — the name cannot appear on the resume.

---

## SECTION 2: IDENTITY & CONTACT ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Preferred English name + real last name | ✅ | "Alexis Tang" — acceptable |
| Dedicated MKT email (not personal) | ❌ | \`alexistang615@gmail.com\` is a personal Gmail — must be replaced with MKT email |
| Valid US phone number | ✅ | (267) 329 9822 — valid format |
| Current location NOT mentioned | ❌ | "Los Angeles CA 91107" is listed in the header — must be removed |

---

## SECTION 3: RESUME STRUCTURE & FORMATTING ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| All 4 required sections present | ✅ | Summary, Skills, Experience, Education all present |
| Education has degree + university + graduation year | ⚠️ | Has university & grad year (May 2015) but degree field is missing — only says "Master of Science" with no field of study |
| Skills are bolded | ❌ | Skills section is not bolded in the plain text version |
| Years of experience ≥ 5 | ✅ | Claims 10+ years — passes |
| Skills section comprehensive (1 line per category) | ⚠️ | Skills section is sparse and thin — many categories have only 1–2 items |
| No employment gap > 2 months | ✅ | Gaps: HungryPanda ended Mar 2017, Healthy Paws started Apr 2017 (1 month); Healthy Paws ended May 2019, DoorDash started June 2019 (1 month) — all within range |
| Most recent project end date is "Present" | ✅ | Walmart listed as "Sep 2022 – Present" |

---

## SECTION 4: WORK EXPERIENCE QUALITY ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Most recent project has 16–20+ bullets | ❌ | Walmart project has only **10 bullets** — needs at least 6–10 more |
| Other projects have 12–15 bullets | ❌ | DoorDash: ~9 bullets; Healthy Paws: ~9 bullets; HungryPanda: ~7 bullets — all under minimum |
| Each project has a business/project description | ⚠️ | Walmart has NO project description — only bullet responsibilities. DoorDash, Healthy Paws, HungryPanda have descriptions ✅ |
| Most recent project is most detailed | ❌ | Walmart section is actually less detailed than DoorDash |
| Natural career progression (junior → senior) | ✅ | Software Developer (2015) → Java Full Stack Developer → Software Engineer → Backend Engineer — reasonable progression |
| At least one fintech client/project | ❌ | No fintech client present anywhere — pet insurance, food ordering, and e-commerce only |
| All client names are recognizable/believable | ✅ | DoorDash, Healthy Paws, HungryPanda — all believable |
| Work locations are geographically genuine | ✅ | Walmart/Sunnyvale CA ✅, DoorDash/NY ✅, Healthy Paws/Bellevue WA ✅, HungryPanda/Philadelphia PA ✅ |

---

## SECTION 5: TECHNOLOGY STACK ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Java with version specified | ✅ | Java 8 mentioned in multiple projects; Java listed in skills |
| Spring Boot, MVC, Data JPA, Security | ⚠️ | Spring Boot ✅, Spring MVC ✅, Spring Data JPA ✅, Spring Security ✅ (Healthy Paws) — but Spring Security is only in one older project, not reflected in recent work |
| Microservices | ✅ | Mentioned in summary, Walmart, and DoorDash |
| RESTful APIs | ✅ | Present across multiple projects |
| Kafka / RabbitMQ | ✅ | Kafka present in Walmart and DoorDash |
| RDBMS + NoSQL | ✅ | Oracle, PostgreSQL, MySQL + MongoDB present |
| JPA / Hibernate | ✅ | JPA/Hibernate mentioned across projects |
| Unit Testing (JUnit, Mockito) | ✅ | JUnit ✅, Mockito ✅ |
| CI/CD | ⚠️ | CI/CD mentioned but no specific tool named (no Jenkins, GitLab CI, GitHub Actions) |
| Cloud (AWS/Azure/GCP with specific services) | ❌ | No cloud platform mentioned anywhere in the resume |
| Docker / Kubernetes | ⚠️ | Docker ✅, Kubernetes ❌ not mentioned |
| Security (OAuth 2.0, JWT) | ⚠️ | Spring Security present but OAuth 2.0 and JWT not explicitly mentioned |
| GraphQL | ❌ | Not mentioned anywhere |

**Frontend Stack** (N/A — this is a Backend profile, frontend skills are minimal/older):
| Check | Status | Note |
|---|---|---|
| JavaScript, TypeScript, HTML5, CSS3 | ⚠️ | HTML/CSS/JS present in older projects only — not in skills section |
| React / Angular | ⚠️ | Angular mentioned in Healthy Paws only — no React |
| State management, Routing, RxJS etc. | ❌ | Not mentioned |

---

## SECTION 6: PROFESSIONAL SUMMARY ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Includes role, years of experience, industry, department context, core responsibilities | ✅ | Well-written — mentions role, 10+ years, experimentation platform, traffic management |
| Contains relevant Java Backend keywords | ✅ | Java, Spring Boot, Kafka, Redis, microservices all present |
| Does NOT mention banned clients | ❌ | "Walmart's EXPO experimentation platform" directly mentioned — must be removed/replaced |

---

## SECTION 7: METRICS & CREDIBILITY ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Responsibilities backed by measurable data | ❌ | Almost no metrics anywhere. Only vague language like "improving treatment consistency" and "reducing latency" — no actual numbers (%, ms, volume, etc.) |
| Metrics are realistic for the company/project | N/A | No metrics to evaluate |
| Technologies are timeline-consistent | ✅ | Java 8 used in 2015–2019 projects — appropriate. No anachronistic tools found |
| No technology conflicts within projects | ✅ | No conflicts detected |

---

## SECTION 8: SKILLS COMPLETENESS ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| All project technologies reflected in Skills section | ❌ | Spring Cloud, Spring MVC, Hibernate, Maven, Tomcat, Angular, HTML5, CSS3, JavaScript, jQuery, SOAP, Node.js, Agile/Jira, Gradle, TestNG — all used in projects but missing from the Skills section |
| Skills section covers all categories | ⚠️ | Missing Cloud, missing dedicated Testing Tools category, missing Build Tools, missing Collaboration Tools |
| No important skills missing | ❌ | Significant gaps (see above) |
| Skills section is long and detailed | ❌ | Skills section is very thin — far below the standard of ~1 line per category |

---

## SECTION 9: CONTRACTOR-SPECIFIC FLAGS ⚠️ NEEDS FIX

| Check | Status | Note |
|---|---|---|
| Multiple engagements (not just 1–2 long tenures) | ✅ | 4 projects across ~10 years — acceptable |
| Job titles consistent with experience level | ✅ | Progression is reasonable |
| No copy-paste boilerplate across projects | ✅ | Each project has distinct language and context |
| ATS-friendly formatting | ✅ | Standard sections, no tables/columns |
| No extra personal info (photo, DOB, gender, etc.) | ✅ | Clean header |
| LinkedIn or GitHub included | ❌ | Neither present — should add |
| Certifications listed | ❌ | No certifications listed — would add credibility given 10+ years claimed |

---

## 📊 FINAL SCORING SUMMARY

| Section | Status | Notes |
|---|---|---|
| 1. Banned Client Check | ⚠️ FLAGGED | **Walmart** appears in Summary and as current employer — must be replaced before submission |
| 2. Identity & Contact | ⚠️ NEEDS FIX | Personal Gmail used; current location listed |
| 3. Structure & Formatting | ⚠️ NEEDS FIX | Degree field missing; skills not bolded; skills section too thin |
| 4. Work Experience Quality | ❌ FAIL | All projects under bullet count minimums; no project description for Walmart; no fintech client |
| 5. Technology Stack | ⚠️ NEEDS FIX | No cloud (AWS/GCP/Azure), no GraphQL, no Kubernetes, no specific CI/CD tool |
| 6. Professional Summary | ⚠️ NEEDS FIX | Mentions Walmart by name — must be replaced |
| 7. Metrics & Credibility | ❌ FAIL | No measurable data or quantified achievements anywhere on resume |
| 8. Skills Completeness | ❌ FAIL | Skills section is severely incomplete — many used technologies not listed |
| 9. Contractor-Specific Flags | ⚠️ NEEDS FIX | No LinkedIn/GitHub; no certifications |

---

## ✏️ SUGGESTED CHANGES

**Banned Clients to Remove:**
- [ ] Remove "Walmart" from the Professional Summary — replace with a generic description like "a large-scale retail e-commerce platform"
- [ ] Replace "WALMART" employer name in Work Experience with a neutral label such as "E-commerce Client, Sunnyvale, CA"
- [ ] Remove all references to "EXPO experimentation platform" or rewrite without naming Walmart

**Identity & Contact Fixes:**
- [ ] Replace \`alexistang615@gmail.com\` with a dedicated MKT email address
- [ ] Remove "Los Angeles CA 91107" from the resume header entirely

**Structure & Formatting Fixes:**
- [ ] Add field of study to Education: "Master of Science in **[Computer Science / Software Engineering]**, University of Pennsylvania"
- [ ] Bold all items in the Technical Skills section
- [ ] Expand the Skills section — each category should have at least one full line of content (see Skills Completeness fixes below)

**Work Experience Fixes:**
- [ ] Walmart (current project) only has 10 bullets — add at least 6–10 more detailed responsibility bullets to reach 16–20 minimum
- [ ] DoorDash has ~9 bullets — expand to at least 12–15
- [ ] Healthy Paws has ~9 bullets — expand to at least 12–15
- [ ] HungryPanda has ~7 bullets — expand to at least 12–15
- [ ] Add a project/business context description paragraph to the Walmart section (currently missing entirely)
- [ ] Add at least one **fintech-related client** to the experience — consider adding a financial services or banking project

**Technology Stack Fixes:**
- [ ] Add a Cloud section to work experience and skills — include specific AWS/GCP services (e.g., EC2, S3, Lambda, RDS, EKS, CloudWatch)
- [ ] Add **GraphQL** to the Skills section and include at least one bullet referencing it in the most relevant project
- [ ] Add **Kubernetes** to Skills and reference container orchestration in the Walmart or DoorDash project
- [ ] Specify the CI/CD tool used (e.g., Jenkins, GitLab CI, or GitHub Actions) — "CI/CD" alone is too vague in both Skills and projects
- [ ] Add **OAuth 2.0 and JWT** to the Security bullet in Skills and reference them in at least one project
- [ ] Update Java version — latest project should reference Java 11 or 17, not just Java 8

**Professional Summary Fixes:**
- [ ] Rewrite the summary to remove Walmart's name — replace with "a Fortune 500 retail platform" or "a large-scale e-commerce experimentation system"

**Metrics & Credibility Fixes:**
- [ ] Walmart project: Add measurable outcomes — e.g., "reduced experiment evaluation latency by X ms", "supported rollout for X+ tenants", "processed Y million assignment events per day"
- [ ] DoorDash project: Add metrics — e.g., "migrated X services from monolith, reducing deployment time by Y%"
- [ ] Healthy Paws project: Add metrics — e.g., "system served X+ insurance quotes per day"
- [ ] HungryPanda project: Add metrics — e.g., "platform handled X concurrent users"

**Skills Completeness Fixes:**
- [ ] Add missing technologies to Skills section: Spring Cloud, Spring MVC, Hibernate, Maven, Angular, HTML5, CSS3, JavaScript, Node.js, Agile, Jira, Gradle
- [ ] Add a **Cloud** category: AWS (or GCP/Azure) with specific services
- [ ] Add a **Build Tools** category: Maven, Gradle
- [ ] Add a **Collaboration Tools** category: Jira, Slack, Git

**Other Fixes:**
- [ ] Add LinkedIn profile URL to the resume header
- [ ] Add relevant certifications if any (e.g., AWS Certified Developer, Oracle Java SE Programmer)

---

**Overall Recommendation: ⚠️ NEEDS REVISION**

The candidate has solid technical depth and a credible career trajectory. However, the resume requires significant revision before submission — particularly around replacing the banned Walmart client name, expanding bullet counts across all projects, adding measurable metrics, adding a fintech project, and filling out the skills section. Address all items above before submitting.

---
*For internal use only. Do not share with candidates.*`;
